import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';

import { Space, SpaceTag } from '@interfaces/space';
import { SpaceService } from '@services/space.service';
import { UserService } from '@services/user.service';
import { NgxSpinnerService } from 'ngx-spinner';

type SpacesState = {
  spaces: Space[];
  selectedSpace: Space | null;
  spaceLoading: boolean;
  spaceError: string | null;
  spacesCount: number;
  lastUpdated: number | null;
  isRefreshing: boolean;
  isInitialLoad: boolean;
  selectedRole: { value: string; label: string };
  selectedSortOption: { value: string; label: string };
  showArchivedSpaces: boolean;
  CACHE_DURATION_MS: number;
  userTags: SpaceTag[];
  tagsFilterList: SpaceTag[];
};

const initialState: SpacesState = {
  spaces: [],
  selectedSpace: null,
  spaceLoading: false,
  spaceError: null,
  spacesCount: 0,
  lastUpdated: null,
  isRefreshing: false,
  isInitialLoad: true,
  selectedRole: { value: 'all', label: 'all' },
  selectedSortOption: { value: 'last_update', label: 'last_update' },
  showArchivedSpaces: false,
  CACHE_DURATION_MS: 5 * 60 * 1000,
  userTags: [],
  tagsFilterList: [],
};

export const SpacesStore = signalStore(
  { providedIn: 'root' },

  withState(initialState),

  withComputed((store) => ({
    filteredSpaces: computed(() => {
      // Initial Filtering (Archived)
      let filtered = store.showArchivedSpaces() ? store.spaces() : store.spaces().filter((space) => !space.is_archived);

      // Role Filtering and Sorting
      if (store.selectedRole().value !== 'all') {
        filtered = filtered.filter((space) => space.role === store.selectedRole().value);
      }

      // Tag Filtering
      if (store.tagsFilterList().length > 0) {
        const tagUuidsToFilter = store.tagsFilterList().map((t) => t.uuid);

        filtered = filtered.filter((space) => space.tags?.some((tag) => tagUuidsToFilter.includes(tag.uuid)));
      }

      // Final Sorting
      const sortOption = store.selectedSortOption().value;

      return filtered.sort((a, b) => {
        // Sort by Name
        if (sortOption === 'name') {
          // Special logic for 'My Space' only applies if 'all' roles are selected
          if (store.selectedRole().value === 'all') {
            if (a.name === 'My Space' && b.name !== 'My Space') return -1;
            if (a.name !== 'My Space' && b.name === 'My Space') return 1;
          }
          return a.name.localeCompare(b.name);
        }

        // Sort by Last Update while always keeping 'My Space' at the top if 'all' roles are selected
        else if (sortOption === 'last_update') {
          if (store.selectedRole().value === 'all') {
            if (a.name === 'My Space' && b.name !== 'My Space') return -1;
            if (a.name !== 'My Space' && b.name === 'My Space') return 1;
          }
          const dateA = new Date(a.updated_at ?? '').getTime();
          const dateB = new Date(b.updated_at ?? '').getTime();
          return dateB - dateA;
        }

        return 0;
      });
    }),
  })),

  withMethods(
    (
      store,
      spaceService = inject(SpaceService),
      userService = inject(UserService),
      spinner = inject(NgxSpinnerService),
    ) => {
      const shouldLoadSpaces = (forceRefresh: boolean): boolean => {
        const now = Date.now();
        const lastUpdated = store.lastUpdated();
        const isStale = !lastUpdated || now - lastUpdated > store.CACHE_DURATION_MS();

        return forceRefresh || store.isInitialLoad() || isStale;
      };

      const setLoadingState = () => {
        if (store.isInitialLoad() || store.spacesCount() === 0) {
          patchState(store, { spaceLoading: true });
        } else {
          patchState(store, { isRefreshing: true });
        }
      };

      const resetLoadingState = (success = false) => {
        patchState(store, {
          spaceLoading: false,
          isRefreshing: false,
          isInitialLoad: false,
          ...(success && { isInitialLoad: false }),
        });
      };

      /**
       * Loads spaces from the server.
       * @param forceRefresh - If true, forces a refresh of the spaces.
       */
      const loadSpaces = (forceRefresh = false) => {
        const user = userService.getUserData();
        if (!user?.uuid) {
          patchState(store, {
            spaceError: 'User not found',
            spaceLoading: false,
          });
          return;
        }

        if (!shouldLoadSpaces(forceRefresh)) {
          return;
        }

        if (store.spaceLoading() || store.isRefreshing()) {
          return;
        }

        setLoadingState();

        if (store.spacesCount() === 0 || store.isInitialLoad() || forceRefresh) {
          spinner.show();
        }
        const paginationParam = `&limit=1000&offset=0&extra=users&is_archived=${
          store.showArchivedSpaces() ? true : false
        }`;
        spaceService.getSpaces(user.uuid, paginationParam).subscribe({
          next: (spaces) => {
            const parsedSpaces = spaces.results.map((space: Space) => ({
              ...space,
              name: space.is_my_space ? 'My Space' : space.name,
            }));
            patchState(store, {
              spaces: sortSpaces(parsedSpaces),
              spacesCount: spaces.count,
              spaceError: null,
              lastUpdated: Date.now(),
            });
          },
          error: (error) => {
            patchState(store, {
              spaceError: error.message || 'Failed to load spaces',
            });
          },
          complete: () => {
            resetLoadingState(true);
            spinner.hide();
          },
        });
        loadUserTags();
      };

      /**
       * Adds a space to the store.
       * @param spaceData: { name: string, company: { uuid: string } } - The space to add to the store.
       */
      const addSpace = async (spaceData: { name: string; company: { uuid: string } }): Promise<Space | undefined> => {
        try {
          const space = await spaceService.createSpace(spaceData).toPromise();
          await loadSpaces(true);
          return space;
        } catch (error) {
          patchState(store, { spaceError: 'Error' });
          return undefined;
        }
      };
      /**
       * Deletes a space from the store.
       * @param spaceId: string - The id of the space to delete.
       */
      const deleteSpace = async (spaceId: string): Promise<void> => {
        spinner.show();
        try {
          await spaceService.deleteSpace(spaceId).toPromise();
          patchState(store, { spaces: store.spaces().filter((space) => space.uuid !== spaceId) });
          await loadSpaces(true);
        } catch (error) {
          patchState(store, { spaceError: 'Error' });
          throw error;
        } finally {
          spinner.hide();
          patchState(store, { spaceLoading: false });
        }
      };

      /**
       * Updates a space in the store.
       * @param spaceId: string - The id of the space to update.
       * @param spaceData: Partial<Space> - The data to update the space with.
       */
      const updateSpace = async (spaceId: string, spaceData: Partial<Space>): Promise<Space> => {
        spinner.show();
        try {
          const space = await spaceService.updateSpace(spaceId, spaceData).toPromise();
          patchState(store, { spaces: store.spaces().map((s) => (s.uuid === spaceId ? space : s)) });
          loadSpaces(true);
          return space;
        } catch (error) {
          patchState(store, { spaceError: 'Error' });
          throw error;
        } finally {
          spinner.hide();
          patchState(store, { spaceLoading: false });
        }
      };

      /**
       * Clones a space in the store.
       * @param cloneData: { space: { uuid: string }, company: { uuid: string }, name: string } - The data to clone the space with.
       * @returns The cloned space or undefined if there was an error.
       */
      const cloneSpace = async (cloneData: {
        space: { uuid: string };
        company: { uuid: string };
        name: string;
      }): Promise<Space | undefined> => {
        spinner.show();
        try {
          const space = await spaceService.cloneSpace(cloneData).toPromise();
          await loadSpaces(true);
          return space;
        } catch (error) {
          patchState(store, { spaceError: 'Error' });
          return undefined;
        } finally {
          spinner.hide();
          patchState(store, { spaceLoading: false });
        }
      };

      /**
       * Loads user's space tags.
       */
      const loadUserTags = (): void => {
        const user = userService.getUserData();

        if (!user?.uuid) {
          return;
        }

        spaceService.getUserTags(user.uuid).subscribe({
          next: (res) => {
            patchState(store, { userTags: res.results });
          },
          error: (err) => {
            patchState(store, { spaceError: 'Error loading user space tags' });
          },
        });
      };

      /**
       * Update that tag.
       * @param tagObj: SpaceTag - The tag object with updated data.
       */
      const updateTag = (tagObj: SpaceTag): Promise<any> => {
        return spaceService
          .updateTag(tagObj.uuid, {
            name: tagObj.name,
            color: tagObj.color,
          })
          .toPromise();
      };

      /**
       * Deletes a tag.
       * @param tagId: string - The id of the tag to delete.
       */
      const deleteTag = (tagId: string): Promise<void> => {
        return spaceService.deleteTag(tagId).toPromise();
      };

      const addToSelectedTagsFilterList = (tag: SpaceTag): void => {
        const currentList = store.tagsFilterList();
        if (!currentList.find((t) => t.uuid === tag.uuid)) {
          patchState(store, { tagsFilterList: [...currentList, tag] });
        }
      };

      const removeFromSelectedTagsFilterList = (tagId: string): void => {
        const currentList = store.tagsFilterList();
        patchState(store, { tagsFilterList: currentList.filter((t) => t.uuid !== tagId) });
      };

      const toggleSelectAllTagsInFilter = (): void => {
        const allTags = store.userTags();
        const selectedTags = store.tagsFilterList();

        if (allTags.length === selectedTags.length) {
          patchState(store, { tagsFilterList: [] });
        } else {
          patchState(store, { tagsFilterList: [...allTags] });
        }
      };

      /**
       * Sorts spaces by name.
       * @param spaces The spaces to sort.
       * @returns The sorted spaces.
       */
      const sortSpaces = (spaces: Space[]): Space[] => {
        return spaces && spaces.length > 0
          ? [...spaces].sort((a, b) => {
              // Prioritize spaces where name is My Space
              if (a.name === 'My Space' && b.name !== 'My Space') return -1;
              if (a.name !== 'My Space' && b.name === 'My Space') return 1;

              // If both are the same in terms of name, sort by name
              return a.name.localeCompare(b.name);
            })
          : [];
      };

      /**
       * Filters spaces based on the selected role.
       * @param role The role to filter spaces by.
       */
      const filterSpacesByRole = (role: string): void => {
        patchState(store, { selectedRole: { value: role, label: role } });
      };
      /**
       * Sorts spaces based on the selected option.
       * @param option The option to sort spaces by ('name' or 'last_update').
       */
      const sortSpacesByOption = (option: string): void => {
        patchState(store, { selectedSortOption: { value: option, label: option } });
      };

      const toggleShowArchivedSpaces = (): void => {
        patchState(store, { showArchivedSpaces: !store.showArchivedSpaces() });
        loadSpaces(true);
      };

      const clearStore = () => {
        patchState(store, initialState);
      };

      return {
        loadSpaces,
        loadUserTags,
        deleteTag,
        updateTag,
        addToSelectedTagsFilterList,
        removeFromSelectedTagsFilterList,
        toggleSelectAllTagsInFilter,
        addSpace,
        deleteSpace,
        updateSpace,
        sortSpaces,
        filterSpacesByRole,
        sortSpacesByOption,
        toggleShowArchivedSpaces,
        cloneSpace,
        clearStore,
      };
    },
  ),
);
